import { useCallback, useEffect, useRef, useState } from 'react';
import { lessonService } from '../services/lessonService';
import { useAuthStore } from '../store/authStore';

interface UseEditLockReturn {
  isLocked: boolean;
  lockedBy: string | null;
  isMyLock: boolean;
  showLockedModal: boolean;
  dismissLockedModal: () => void;
}

const POLL_INTERVAL = 60_000;

export function useEditLock(lessonId: string): UseEditLockReturn {
  const user = useAuthStore((s) => s.user);
  const [isLocked, setIsLocked] = useState(false);
  const [lockedBy, setLockedBy] = useState<string | null>(null);
  const [isMyLock, setIsMyLock] = useState(false);
  const [showLockedModal, setShowLockedModal] = useState(false);
  const acquiredRef = useRef(false);

  const checkLockStatus = useCallback(async () => {
    try {
      const { lesson } = await lessonService.getById(lessonId);
      if (lesson.locked_by) {
        const mine = lesson.locked_by === user?.id;
        setIsLocked(true);
        setLockedBy(lesson.locked_by);
        setIsMyLock(mine);
        if (!mine && !acquiredRef.current) {
          setShowLockedModal(true);
        }
      } else {
        setIsLocked(false);
        setLockedBy(null);
        setIsMyLock(false);
      }
    } catch {
      // ignore polling errors
    }
  }, [lessonId, user?.id]);

  const acquireLock = useCallback(async () => {
    try {
      await lessonService.acquireLock(lessonId);
      acquiredRef.current = true;
      setIsLocked(true);
      setLockedBy(user?.id ?? null);
      setIsMyLock(true);
    } catch (_err: unknown) {
      // Lock acquisition failed — another user holds it
      await checkLockStatus();
    }
  }, [lessonId, user?.id, checkLockStatus]);

  const releaseLock = useCallback(async () => {
    if (!acquiredRef.current) return;
    try {
      await lessonService.releaseLock(lessonId);
      acquiredRef.current = false;
    } catch {
      // ignore release errors
    }
  }, [lessonId]);

  useEffect(() => {
    acquireLock();

    const pollTimer = setInterval(async () => {
      if (acquiredRef.current) {
        // Re-acquire to refresh the lock TTL
        try {
          await lessonService.acquireLock(lessonId);
        } catch {
          await checkLockStatus();
        }
      } else {
        await checkLockStatus();
      }
    }, POLL_INTERVAL);

    const handleBeforeUnload = () => {
      releaseLock();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearInterval(pollTimer);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      releaseLock();
    };
  }, [lessonId, acquireLock, releaseLock, checkLockStatus]);

  const dismissLockedModal = useCallback(() => {
    setShowLockedModal(false);
  }, []);

  return { isLocked, lockedBy, isMyLock, showLockedModal, dismissLockedModal };
}
